import BarChartIcon from '@mui/icons-material/BarChart';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import MasterFilter, {
  type DateRangeValue,
  type FilterFieldConfig,
  type FilterOption,
  useMasterFilter,
} from 'components/common/MasterFilter';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import moment from 'moment';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useGetSurveyStatsQuery, useGetSurveysQuery } from 'services/api/surveys.api';
import type { Survey } from 'services/types/survey';
import {
  MOBILE_PAGE_PT,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SURFACE,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';

import SurveysBranchMatrix from './components/SurveysBranchMatrix';
import SurveysDashboard from './components/SurveysDashboard';
import SurveysResultsTable from './components/SurveysResultsTable';

const TOGGLE_GROUP_SX = {
  '& .MuiToggleButton-root': {
    fontFamily: 'Jost, sans-serif',
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'none',
    borderRadius: RADIUS_ITEM,
    border: 'none',
    color: `${NAVY}99`,
    backgroundColor: SURFACE_CONTAINER_LOW,
    px: 2,
    py: 0.75,
    transition: 'all 0.2s ease-in-out',
    '&.Mui-selected': {
      color: NAVY,
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      boxShadow: '0px 2px 8px rgba(21, 24, 58, 0.08)',
    },
    '&:hover': {
      backgroundColor: `${NAVY}0A`,
    },
  },
  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    borderLeft: 'none',
    ml: 0.5,
  },
  '& .MuiToggleButtonGroup-grouped:first-of-type': {
    borderRadius: RADIUS_ITEM,
  },
  '& .MuiToggleButtonGroup-grouped:last-of-type': {
    borderRadius: RADIUS_ITEM,
  },
} as const;

const surveysFilterConfig: FilterFieldConfig[] = [
  {
    key: 'dateRange',
    kind: 'dateRange',
    label: 'Fecha',
    placement: 'bar',
    defaultValue: { start: moment().startOf('isoWeek'), end: moment().endOf('isoWeek') },
  },
  {
    key: 'surveyNames',
    kind: 'multiSelect',
    label: 'Encuesta',
    placement: 'bar',
  },
];

const SurveysResults: React.FC = () => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const businessUnitId =
    selectedBusinessUnitId !== null && selectedBusinessUnitId !== -1 ? selectedBusinessUnitId : null;

  const filter = useMasterFilter(surveysFilterConfig, businessUnitId);

  const dateRange = (filter.values.dateRange as DateRangeValue | undefined) ?? { start: null, end: null };
  const startDate = dateRange.start;
  const endDate = dateRange.end;
  const selectedSurveyNames = (filter.values.surveyNames as string[] | undefined) ?? [];

  const [viewMode, setViewMode] = useState<'chart' | 'list'>('chart');
  const [listSubTab, setListSubTab] = useState(0);

  const { data: allSurveysData, isLoading } = useGetSurveysQuery(
    {
      businessUnitId: businessUnitId || undefined,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    },
    { skip: !businessUnitId }
  );

  const { data: surveyStats, isLoading: isLoadingStats } = useGetSurveyStatsQuery(
    {
      businessUnitId: businessUnitId ?? 0,
      startDate: startDate?.toISOString() ?? '',
      endDate: endDate?.toISOString() ?? '',
      surveyNames: selectedSurveyNames.length > 0 ? selectedSurveyNames : undefined,
    },
    { skip: !businessUnitId || !startDate || !endDate }
  );

  const surveyNameOptions: FilterOption[] = useMemo(() => {
    if (!allSurveysData?.surveys) return [];
    const uniqueNames = [...new Set(allSurveysData.surveys.map((s) => s.name).filter(Boolean))];
    return uniqueNames.map((name) => ({ value: name, label: name }));
  }, [allSurveysData?.surveys]);

  const dynamicOptions = useMemo(() => ({ surveyNames: surveyNameOptions }), [surveyNameOptions]);

  const filteredSurveys = useMemo(() => {
    if (!allSurveysData?.surveys) return [];
    if (selectedSurveyNames.length === 0) return allSurveysData.surveys;
    return allSurveysData.surveys.filter((survey) => selectedSurveyNames.includes(survey.name));
  }, [allSurveysData?.surveys, selectedSurveyNames]);

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'chart' | 'list' | null) => {
    if (newMode !== null) setViewMode(newMode);
  };

  const handleSurveyClick = (survey: Survey) => {
    if (survey.clientId) window.open(`/clients/${survey.clientId}`, '_blank', 'noopener,noreferrer');
  };

  if (!businessUnitId) {
    return (
      <Box sx={{ px: 2, pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2 }, backgroundColor: SURFACE, minHeight: '100vh' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 120px)',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '1.125rem',
              color: NAVY,
              mb: 1,
            }}
          >
            Selecciona una unidad de negocio
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.95rem',
              color: `${NAVY}99`,
            }}
          >
            Escoge una unidad de negocio en la barra lateral para visualizar los resultados de encuestas.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: SURFACE,
      }}
    >
      {/* Filter bar */}
      <Box sx={{ pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2 }, px: { xs: 1, md: 2 }, flexShrink: 0 }}>
        <MasterFilter {...filter} dynamicOptions={dynamicOptions} />
      </Box>

      {/* View mode toggle */}
      <Box sx={{ px: { xs: 1, md: 2 }, pt: 1.5 }}>
        <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small" sx={TOGGLE_GROUP_SX}>
          <ToggleButton value="chart">
            <BarChartIcon sx={{ mr: 0.75, fontSize: '1.1rem' }} />
            Gráficos
          </ToggleButton>
          <ToggleButton value="list">
            <FormatListBulletedIcon sx={{ mr: 0.75, fontSize: '1.1rem' }} />
            Lista
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: { xs: 1, md: 2 }, py: 1.5 }}>
        {viewMode === 'chart' ? (
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
              p: 3,
              flex: 1,
              overflow: 'auto',
            }}
          >
            <SurveysDashboard
              surveys={filteredSurveys}
              isLoading={isLoading}
              surveyStats={surveyStats}
              isLoadingStats={isLoadingStats}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
              <ToggleButtonGroup
                value={listSubTab}
                exclusive
                onChange={(_, v) => {
                  if (v !== null) setListSubTab(v);
                }}
                size="small"
                sx={TOGGLE_GROUP_SX}
              >
                <ToggleButton value={0}>General</ToggleButton>
                <ToggleButton value={1}>Por Sucursal</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {listSubTab === 0 && (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <SurveysResultsTable surveys={filteredSurveys} onSurveyClick={handleSurveyClick} />
              </Box>
            )}

            {listSubTab === 1 && (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <SurveysBranchMatrix data={surveyStats?.chartData.byBranchOffice} isLoading={isLoadingStats} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SurveysResults;
